import React from 'react';
import {
  Box, Container, Grid, Card, CardContent, Typography, Button,
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { departments } from '../data/hospitalData';
import PageHero from '../components/common/PageHero';

const DepartmentsPage: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  return (
    <Box>
      <PageHero
        title={t.departments.title}
        subtitle={t.departments.subtitle}
        breadcrumbs={[
          { label: language === 'en' ? 'Home' : 'Accueil', path: '/' },
          { label: t.nav.departments },
        ]}
      />
      <Container maxWidth="xl" sx={{ py: 8 }}>
        <Grid container spacing={3}>
          {departments.map((dept) => (
            <Grid item xs={12} sm={6} md={4} key={dept.id}>
              <Card
                onClick={() => navigate(`/departments/${dept.id}`)}
                sx={{
                  height: '100%',
                  borderLeft: `4px solid ${dept.color}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 8 },
                }}
              >
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        width: 52, height: 52,
                        bgcolor: `${dept.color}15`,
                        borderRadius: 2,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '1.6rem',
                        flexShrink: 0,
                      }}
                    >
                      {dept.icon}
                    </Box>
                    <Typography variant="h6" fontWeight={700} lineHeight={1.3} fontSize="1rem">
                      {language === 'en' ? dept.nameEn : dept.nameFr}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.8} flexGrow={1} mb={2.5}>
                    {language === 'en' ? dept.descEn : dept.descFr}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<ArrowForward />}
                      onClick={(e) => { e.stopPropagation(); navigate(`/departments/${dept.id}`); }}
                      sx={{ borderColor: dept.color, color: dept.color, textTransform: 'none', fontSize: '0.78rem', '&:hover': { bgcolor: `${dept.color}10` } }}
                    >
                      {language === 'en' ? 'Learn more' : 'En savoir plus'}
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      onClick={(e) => { e.stopPropagation(); navigate('/appointment'); }}
                      sx={{ color: 'text.secondary', textTransform: 'none', fontSize: '0.78rem' }}
                    >
                      {t.common.bookAppointment}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* CTA */}
        <Box
          sx={{
            mt: 6, p: 4, borderRadius: 3,
            background: 'linear-gradient(135deg, #0B4F8A, #00897B)',
            textAlign: 'center', color: 'white',
          }}
        >
          <Typography variant="h5" fontWeight={700} mb={1}>
            {language === 'en' ? "Don't see what you're looking for?" : 'Vous ne trouvez pas ce que vous cherchez ?'}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 3 }}>
            {language === 'en'
              ? 'Contact us and our team will direct you to the right department.'
              : 'Contactez-nous et notre équipe vous orientera vers le bon service.'}
          </Typography>
          <Button
            variant="contained"
            sx={{ bgcolor: 'white', color: 'primary.dark', '&:hover': { bgcolor: 'grey.100' } }}
            onClick={() => navigate('/contact')}
          >
            {t.common.contactUs}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default DepartmentsPage;
